'use client'

import { useCallback, useEffect, useMemo, useRef } from 'react'

import { ColumnFiltersState, OnChangeFn } from '@tanstack/react-table'
import { compact, isError, uniq } from 'lodash'
import { usePathname, useSearchParams } from 'next/navigation'

import {
  buildTokenFilterKey,
  COLUMN_IDS,
  DirectionAndValueFilterValue,
  directionFromFilter,
  directionToFilter,
  parseDirectionAndValue,
  parseTokenFilterKey,
} from '~/app/accounting/filters/helpers'
import {
  DEFAULT_PERIOD,
  Period,
  getPeriodLabel,
  usePeriodPresets,
} from '~/app/accounting/period-utils'
import { useImportedAccounts } from '~/app/accounting/useImportedAccounts'
import { parseMultiSelectFilterValue } from '~/components/Table'
import { useActiveOrgId } from '~/hooks/org'
import { useAccountingTimezone } from '~/hooks/useAccountingTimezone'
import { useSmartAccounts } from '~/hooks/useSmartAccounts'
import {
  AccountingTz,
  endOfDay,
  formatYmdInTz,
  parseYmdInTz,
  sameDayInTz,
  startOfMonth,
} from '~/utils/timezone'

const PARAM_KEYS = {
  PERIOD: 'period',
  START_DATE: 'startDate',
  END_DATE: 'endDate',
  ADDRESSES: 'addresses',
  CHAIN_IDS: 'chainIds',
  TOKENS: 'tokens',
  DIRECTION: 'direction',
  MIN_AMOUNT: 'minAmount',
  MAX_AMOUNT: 'maxAmount',
  MEMO: 'memo',
  TRANSACTION_HASH: 'transactionHash',
} as const

const ALL_TIME_PARAM_VALUE = 'all'

// Kept out of PARAM_KEYS/FILTER_PARAM_KEYS on purpose: tab lives in the URL but
// isn't a filter, so the LS restore mustn't count ?tab= as "URL has filters".
const TAB_PARAM = 'tab'
const TAB_VALUES = [
  'transactions',
  'tokens',
  'lots',
  'imported',
  'reports',
] as const
export type AccountingTab = (typeof TAB_VALUES)[number]

const getTabValue = (tab: string | null): AccountingTab =>
  TAB_VALUES.includes(tab as AccountingTab)
    ? (tab as AccountingTab)
    : 'transactions'

const FILTER_PARAM_KEYS = Object.values(PARAM_KEYS)
const STORAGE_KEY = (orgId: string) => `splits.accountingFilters.${orgId}`

const extractFilterQueryString = (params: {
  get: (key: string) => string | null
}): string => {
  const filterOnly = new URLSearchParams()
  FILTER_PARAM_KEYS.forEach((k) => {
    const v = params.get(k)
    if (v) filterOnly.set(k, v)
  })
  return filterOnly.toString()
}

// localStorage can throw in private/embedded browsers — fail soft.
const safeLocalGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const safeLocalSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value)
  } catch {
    // ignore quota/disabled-storage errors
  }
}

const parseStartDate = (
  dateStr: string | null,
  tz: AccountingTz,
): Date | undefined => {
  if (!dateStr) return undefined
  const d = parseYmdInTz(dateStr, tz)
  return isNaN(d.getTime()) ? undefined : d
}

// End dates are inclusive: 23:59:59.999 in the active tz on the picked day.
const parseEndDate = (
  dateStr: string | null,
  tz: AccountingTz,
): Date | undefined => {
  if (!dateStr) return undefined
  const d = parseYmdInTz(dateStr, tz)
  return isNaN(d.getTime()) ? undefined : endOfDay(d, tz)
}

const endOfToday = (tz: AccountingTz): Date => endOfDay(new Date(), tz)

const derivePeriodFromParams = (
  searchParams: URLSearchParams,
  presets: Period[][],
  tz: AccountingTz,
): Period | null => {
  if (searchParams.get(PARAM_KEYS.PERIOD) === ALL_TIME_PARAM_VALUE) {
    return DEFAULT_PERIOD
  }

  const startDate = parseStartDate(searchParams.get(PARAM_KEYS.START_DATE), tz)
  // If startDate is set but endDate is omitted, treat as "up to now" by populating today.
  const endDate =
    parseEndDate(searchParams.get(PARAM_KEYS.END_DATE), tz) ??
    (startDate ? endOfToday(tz) : undefined)

  if (!startDate && !endDate) {
    // Default to current month, computed in the active tz.
    const now = new Date()
    return {
      startDate: startOfMonth(now, tz),
      endDate: endOfToday(tz),
      label: 'This month',
    }
  }

  const matchedPreset = presets
    .flat()
    .find(
      (preset) =>
        sameDayInTz(startDate, preset.startDate, tz) &&
        sameDayInTz(endDate, preset.endDate, tz),
    )
  if (matchedPreset) return matchedPreset

  return {
    startDate,
    endDate,
    label: getPeriodLabel({ startDate, endDate }),
  }
}

const deriveColumnFiltersFromParams = (
  searchParams: URLSearchParams,
  addressToId: Map<string, string>,
): ColumnFiltersState => {
  const filters: ColumnFiltersState = []

  const addresses = searchParams.get(PARAM_KEYS.ADDRESSES)
  if (addresses) {
    const ids = addresses
      .split(',')
      .filter(Boolean)
      .map((addr) => addressToId.get(addr.toLowerCase()))
      .filter((id): id is string => !!id)
    if (ids.length > 0) {
      filters.push({
        id: COLUMN_IDS.SMART_ACCOUNT_ID,
        value: JSON.stringify(ids),
      })
    }
  }

  const directionParam = searchParams.get(PARAM_KEYS.DIRECTION)
  const direction =
    directionParam === 'in' || directionParam === 'out' ? directionParam : null
  const minAmount = searchParams.get(PARAM_KEYS.MIN_AMOUNT)
  const maxAmount = searchParams.get(PARAM_KEYS.MAX_AMOUNT)
  if (direction || minAmount || maxAmount) {
    filters.push({
      id: COLUMN_IDS.USD_VALUE,
      value: JSON.stringify({
        min: minAmount ?? '',
        max: maxAmount ?? '',
        direction: directionToFilter(direction ?? 'any'),
      } satisfies DirectionAndValueFilterValue),
    })
  }

  const chainIds = searchParams.get(PARAM_KEYS.CHAIN_IDS)
  if (chainIds) {
    const ids = chainIds.split(',').filter(Boolean)
    if (ids.length > 0) {
      filters.push({
        id: COLUMN_IDS.CHAIN_ID,
        value: JSON.stringify(ids),
      })
    }
  }

  const tokens = searchParams.get(PARAM_KEYS.TOKENS)
  if (tokens) {
    // Hand-edited URLs can carry junk addresses (which throw downstream),
    // stray casing, or duplicates — normalize and drop the invalid.
    const keys = uniq(
      compact(
        tokens.split(',').map((key) => {
          const parsed = parseTokenFilterKey(key.toLowerCase())
          return parsed && buildTokenFilterKey(parsed)
        }),
      ),
    )
    if (keys.length > 0) {
      filters.push({
        id: COLUMN_IDS.TOKEN,
        value: JSON.stringify(keys),
      })
    }
  }

  const memo = searchParams.get(PARAM_KEYS.MEMO)
  if (memo) {
    filters.push({ id: COLUMN_IDS.MEMO, value: memo })
  }

  const transactionHash = searchParams.get(PARAM_KEYS.TRANSACTION_HASH)
  if (transactionHash) {
    filters.push({ id: COLUMN_IDS.TRANSACTION_HASH, value: transactionHash })
  }

  return filters
}

const serializeColumnFiltersToParams = (
  filters: ColumnFiltersState,
  params: URLSearchParams,
  idToAddress: Map<string, string>,
) => {
  params.delete(PARAM_KEYS.ADDRESSES)
  params.delete(PARAM_KEYS.CHAIN_IDS)
  params.delete(PARAM_KEYS.TOKENS)
  params.delete(PARAM_KEYS.DIRECTION)
  params.delete(PARAM_KEYS.MIN_AMOUNT)
  params.delete(PARAM_KEYS.MAX_AMOUNT)
  params.delete(PARAM_KEYS.MEMO)
  params.delete(PARAM_KEYS.TRANSACTION_HASH)

  const accountFilter = filters.find(
    (f) => f.id === COLUMN_IDS.SMART_ACCOUNT_ID,
  )
  if (accountFilter?.value) {
    const parsedIds = parseMultiSelectFilterValue(accountFilter.value)
    if (!isError(parsedIds) && parsedIds.length > 0) {
      const addresses = parsedIds
        .map((id) => idToAddress.get(id))
        .filter((addr): addr is string => !!addr)
      if (addresses.length > 0) {
        params.set(PARAM_KEYS.ADDRESSES, addresses.join(','))
      }
    }
  }

  const chainFilter = filters.find((f) => f.id === COLUMN_IDS.CHAIN_ID)
  if (chainFilter?.value) {
    const parsedIds = parseMultiSelectFilterValue(chainFilter.value)
    if (!isError(parsedIds) && parsedIds.length > 0) {
      params.set(PARAM_KEYS.CHAIN_IDS, parsedIds.join(','))
    }
  }

  const tokenFilter = filters.find((f) => f.id === COLUMN_IDS.TOKEN)
  if (tokenFilter?.value) {
    const parsedKeys = parseMultiSelectFilterValue(tokenFilter.value)
    if (!isError(parsedKeys) && parsedKeys.length > 0) {
      params.set(PARAM_KEYS.TOKENS, parsedKeys.join(','))
    }
  }

  const valueFilter = filters.find((f) => f.id === COLUMN_IDS.USD_VALUE)
  if (valueFilter?.value) {
    const parsed = parseDirectionAndValue(valueFilter.value)
    if (!isError(parsed)) {
      const dir = directionFromFilter(parsed)
      if (dir !== 'any') {
        params.set(PARAM_KEYS.DIRECTION, dir)
      }
      if (parsed.min) params.set(PARAM_KEYS.MIN_AMOUNT, parsed.min)
      if (parsed.max) params.set(PARAM_KEYS.MAX_AMOUNT, parsed.max)
    }
  }

  const memoFilter = filters.find((f) => f.id === COLUMN_IDS.MEMO)
  if (typeof memoFilter?.value === 'string' && memoFilter.value) {
    params.set(PARAM_KEYS.MEMO, memoFilter.value)
  }

  const transactionHashFilter = filters.find(
    (f) => f.id === COLUMN_IDS.TRANSACTION_HASH,
  )
  if (
    typeof transactionHashFilter?.value === 'string' &&
    transactionHashFilter.value
  ) {
    params.set(PARAM_KEYS.TRANSACTION_HASH, transactionHashFilter.value)
  }
}

// Re-add the trailing slash usePathname() drops so the written URL stays
// canonical under `trailingSlash: true` (else a later reload 308s into a hard
// navigation).
const ensureTrailingSlash = (path: string) =>
  path.endsWith('/') ? path : `${path}/`

// Merge the saved-filter blob over the current params (keeps ?tab= etc.).
const withStoredFilters = (
  current: URLSearchParams,
  stored: string,
): URLSearchParams => {
  const merged = new URLSearchParams(current.toString())
  new URLSearchParams(stored).forEach((value, key) => merged.set(key, value))
  return merged
}

export const useAccountingSearchParams = () => {
  const [tz] = useAccountingTimezone()
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const orgId = useActiveOrgId()
  // Ids missing from the id <-> address maps are silently dropped in the URL
  // round-trip, so cover archived, automation and imported accounts too.
  const { smartAccounts } = useSmartAccounts({
    showArchived: true,
    includeAutomations: true,
  })
  const { importedAccounts } = useImportedAccounts()
  const periodPresets = usePeriodPresets()

  const accounts = [...smartAccounts, ...importedAccounts]
  const smartAccountsKey = accounts.map((a) => `${a.id}:${a.address}`).join(',')

  const { addressToId, idToAddress } = useMemo(() => {
    const a2i = new Map(accounts.map((a) => [a.address.toLowerCase(), a.id]))
    const i2a = new Map(accounts.map((a) => [a.id, a.address]))
    return { addressToId: a2i, idToAddress: i2a }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartAccountsKey])

  // Per org: restore saved filters when the URL has none, then mirror changes
  // back to localStorage. The ref re-runs the restore on org switch.
  const lastRestoredOrgIdRef = useRef<string | null>(null)
  // eslint-disable-next-line local/noUseEffect -- restore + mirror filters to localStorage
  useEffect(() => {
    if (!orgId) return
    const key = STORAGE_KEY(orgId)
    if (lastRestoredOrgIdRef.current !== orgId) {
      lastRestoredOrgIdRef.current = orgId
      const stored = safeLocalGet(key)
      const urlHasFilters = FILTER_PARAM_KEYS.some((k) => searchParams.has(k))
      if (stored && !urlHasFilters) {
        // Restore saved filters but keep current non-filter params (e.g. ?tab=).
        const restored = withStoredFilters(searchParams, stored)
        window.history.replaceState(
          null,
          '',
          `${ensureTrailingSlash(pathname)}?${restored.toString()}`,
        )
        return
      }
    }
    const filterQs = extractFilterQueryString(searchParams)
    if (safeLocalGet(key) !== filterQs) {
      safeLocalSet(key, filterQs)
    }
  }, [searchParams, orgId, pathname])

  // Until the first restore lands, seed filters from storage so the first paint
  // matches the restored state instead of flashing defaults; the URL is
  // authoritative after (the ref guard keeps a filter-clear from being undone).
  // Storage-read-in-render is safe: this hook only runs in the never-SSR'd page.
  const effectiveSearchParams = useMemo(() => {
    if (!orgId || lastRestoredOrgIdRef.current === orgId) return searchParams
    if (FILTER_PARAM_KEYS.some((k) => searchParams.has(k))) return searchParams
    const stored = safeLocalGet(STORAGE_KEY(orgId))
    return stored ? withStoredFilters(searchParams, stored) : searchParams
  }, [searchParams, orgId])

  const period = useMemo(
    () => derivePeriodFromParams(effectiveSearchParams, periodPresets, tz),
    [effectiveSearchParams, periodPresets, tz],
  )

  const columnFilters = useMemo(
    () => deriveColumnFiltersFromParams(effectiveSearchParams, addressToId),
    [effectiveSearchParams, addressToId],
  )

  // Reads the live URL rather than the useSearchParams snapshot: successive
  // writes can't clobber each other, and the identity survives query changes
  // (callbacks built from it feed column defs, which must not churn per write).
  const replaceParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      const newParams = new URLSearchParams(window.location.search)
      const oldQs = newParams.toString()
      updater(newParams)
      const newQs = newParams.toString()
      if (newQs === oldQs) return
      const base = ensureTrailingSlash(pathname)
      // Filters/tabs are same-page client state, so avoid a route transition
      // and metadata refresh for every query-only change.
      window.history.replaceState(null, '', newQs ? `${base}?${newQs}` : base)
    },
    [pathname],
  )

  const setPeriod = useCallback(
    (newPeriod: Period | null, overrideTz?: AccountingTz) => {
      const activeTz = overrideTz ?? tz
      replaceParams((params) => {
        params.delete(PARAM_KEYS.START_DATE)
        params.delete(PARAM_KEYS.END_DATE)
        params.delete(PARAM_KEYS.PERIOD)

        if (!newPeriod || newPeriod.label === DEFAULT_PERIOD.label) {
          // Explicitly mark "All time" so it persists through refresh
          params.set(PARAM_KEYS.PERIOD, ALL_TIME_PARAM_VALUE)
        } else {
          if (newPeriod.startDate) {
            params.set(
              PARAM_KEYS.START_DATE,
              formatYmdInTz(newPeriod.startDate, activeTz),
            )
          }
          // Omit endDate from URL when it's today (in tz) — lets "up to now"
          // presets re-match after day-rollover on bookmarked URLs.
          if (
            newPeriod.endDate &&
            !sameDayInTz(newPeriod.endDate, new Date(), activeTz)
          ) {
            params.set(
              PARAM_KEYS.END_DATE,
              formatYmdInTz(newPeriod.endDate, activeTz),
            )
          }
        }
      })
    },
    [replaceParams, tz],
  )

  const setColumnFilters: OnChangeFn<ColumnFiltersState> = useCallback(
    (updater) => {
      replaceParams((params) => {
        const currentFilters = deriveColumnFiltersFromParams(
          searchParams,
          addressToId,
        )
        const newFilters =
          typeof updater === 'function' ? updater(currentFilters) : updater
        serializeColumnFiltersToParams(newFilters, params, idToAddress)
      })
    },
    [replaceParams, searchParams, addressToId, idToAddress],
  )

  // Tab shares the URL writer with period/filters so the two can't desync.
  const tab = useMemo(
    () => getTabValue(searchParams.get(TAB_PARAM)),
    [searchParams],
  )

  // No filter cleanup here: the value filter is Transactions-only and the
  // other tabs ignore it, so it's preserved across tabs.
  const setTab = useCallback(
    (value: string) =>
      replaceParams((params) => params.set(TAB_PARAM, getTabValue(value))),
    [replaceParams],
  )

  // Single URL write on purpose: a setColumnFilters + setTab pair would race —
  // the second call reads useSearchParams() before the first write lands.
  const filterTransactionsByToken = useCallback(
    (tokenKey: string) => {
      replaceParams((params) => {
        params.set(PARAM_KEYS.TOKENS, tokenKey)
        params.set(TAB_PARAM, 'transactions')
      })
    },
    [replaceParams],
  )

  const openLotsForToken = useCallback(
    (tokenKey: string) => {
      replaceParams((params) => {
        params.set(PARAM_KEYS.TOKENS, tokenKey)
        params.set(TAB_PARAM, 'lots')
      })
    },
    [replaceParams],
  )

  return {
    period,
    setPeriod,
    columnFilters,
    setColumnFilters,
    tab,
    setTab,
    filterTransactionsByToken,
    openLotsForToken,
  }
}
